import axios, { AxiosRequestConfig } from '@sas/nova-core/axios';
import { getLogger } from '@sas/nova-core/logging';

import { SelfLink } from '../../../catalog/foundation/representations';
import {
  EntityMediaTypeJSON,
  EntityRepr,
  EntityReprCollection,
} from '../../../catalog/instance/representations';

import { HeaderAcceptItem } from '../../../sonder/http/headers';
import {
  CollectionMediaTypeJSON,
  Link,
} from '../../../sonder/rest/representations/commons/commons';

import { DataSourcesProviderCas, DataSourcesProviderCompute } from '../data/api';
import { Entity } from '../../representations/catalog';
import {
  Library,
  TableHistory,
  TableHistoryAnalysisTimeStamp,
  TableHistoryCompletenessPercent,
  TableHistoryRowCount,
} from '../../representations/data';
import { GetLinkCollectionWithQuery } from '../rest/helper';
import { GO_MAX_INT32 } from '../../util/runtime';
import { EntityReprToEntity, EntityToLibrary } from './conv';

// eslint-disable-next-line @sas/i18n/jsx-to-localize
const logger = getLogger('DataSelection');

const catalogURIPrefix = '/catalog/';
export const DataStoreToDataSetRelID = '4b114f6e-1c2a-4060-9184-6809a612f27b';
export const CasTableEntityTypeID = '3a0d5d7b-a1c5-44c6-bfad-0d2174236172';
export const ComputeTableEntityTypeID = '02b7102c-e997-465d-9f41-2491c3a4f05b';
export const ReferenceEntityTypeID = 'c12246d9-75ca-4c83-a702-1a181faead07';

const directEntityRegex = /^.+\/instances\/[^/?]+\/?(?:\?.*)?$/;

// 102
// GetLibraryEntity returns the library associated with the given dataset entity id
export const GetLibraryEntity = async (
  datasetEntityID: string,
  dataProvider?: string
): Promise<[Library | undefined, Error?]> => {
  let typeID = CasTableEntityTypeID;
  if (dataProvider === DataSourcesProviderCompute) {
    typeID = ComputeTableEntityTypeID;
  } else if (dataProvider === DataSourcesProviderCas) {
    typeID = CasTableEntityTypeID;
  } else {
    logger.debug(
      `Unexpected data provider value ${dataProvider}.  Defaulting to ${DataSourcesProviderCas}`
    );
  }
  const libraryEntityURI = `/catalog/definitions/${typeID}/instances/${datasetEntityID}/related/${DataStoreToDataSetRelID}/peers`;
  const [libraryEntity, err] = await GetEntity(libraryEntityURI, { inverse: 'true' });
  if (err) {
    logger.debug(
      `Error returned while getting the library entity for dataset with id ${datasetEntityID}: ${err}`
    );
    return [undefined, err];
  }
  return [EntityToLibrary(libraryEntity!), undefined];
};

// 121
// GetEntity returns the Entity from the given entityURI (i.e. /catalog/definitions/{id}/instances/{id})
export const GetEntity = async (
  entityURI: string,
  queryParams?: Record<string, string>
): Promise<[Entity | undefined, Error?]> => {
  const [entities, err] = await getEntities(entityURI, queryParams);
  if (err) {
    return [undefined, err];
  }
  if (entities && entities.length > 1) {
    logger.debug(
      `${entities.length} entities were returned for query to ${entityURI}.  The first response will be returned`
    );
  }
  // we expect getEntities to return an error if the slice has zero entries.
  return [entities && entities[0], undefined];
};

// 134
// getEntities returns the (possibly multiple) Entities from the given entityURI (i.e. /catalog/definitions/{id}/instances/{id})
// an error will be returned if getEntities does not return at least one entity
const getEntities = async (
  entityURI: string,
  queryParams?: Record<string, string>
): Promise<[Entity[] | undefined, Error?]> => {
  let etag: string | undefined;
  const err = validateURI(entityURI);
  logger.debug(`Preparing to get entity with URI ${entityURI}`);
  if (err !== undefined) {
    logger.error(
      `The provided URI "${entityURI}" does not begin with the prefix "${catalogURIPrefix}"`
    );
    return [undefined, err];
  }
  // if the entityURI contains query parameters, keep those by putting them into the query parameters map
  const qix = entityURI.lastIndexOf('?');
  if (qix > -1) {
    if (!queryParams) {
      queryParams = {};
    }
    const pathParams = entityURI.substring(qix + 1);
    entityURI = entityURI.substring(0, qix);
    const kvs = pathParams.split('&');
    for (const keqv of kvs) {
      const kv = keqv.split('=');
      const kvlen = kv.length;
      if (kvlen > 0) {
        // the parameters in the map take precedence, so check for existence of a key before
        // writing the value
        const found = queryParams[kv[0]];
        if (!found) {
          logger.debug(
            `Query parameter ${kv[0]} from the entity URI has been overridden by a provided query parameter`
          );
          queryParams[kv[0]] = '';

          if (kvlen > 1) {
            logger.debug(`Applying query parameter ${kv[0]} from the entity URI`);
            queryParams[kv[0]] = kv[1];
          }
        }
      }
    }
  }
  const possibleCollection = !isDirectInstanceURI(entityURI);

  const requestConfig: AxiosRequestConfig = {
    method: 'GET',
    url: entityURI,
  };

  if (!possibleCollection) {
    requestConfig.headers = { Accept: EntityMediaTypeJSON };
  } else {
    requestConfig.headers = {
      Accept: CollectionMediaTypeJSON,
      [HeaderAcceptItem]: EntityMediaTypeJSON,
    };
  }

  if (queryParams && Object.entries(queryParams).length > 0) {
    logger.debug(
      `Adding ${Object.entries(queryParams).length} query parameters to uri ${entityURI}`
    );
    requestConfig.params = queryParams;
  }

  let response;
  try {
    response = await axios(requestConfig);
  } catch (error: any) {
    logger.error(error);
  }

  if (response) {
    let catalogEntities: EntityRepr[] = [];
    if (!possibleCollection) {
      if (response.headers.etag) {
        etag = response.headers.etag;
      }
      const entityRepr = response.data as EntityRepr;
      catalogEntities.push(entityRepr);
    } else {
      const collection = response.data as EntityReprCollection;
      catalogEntities = collection.items;
    }
    if (!catalogEntities || catalogEntities.length === 0) {
      logger.debug(`The query to ${entityURI} returned no entities`);
      return [undefined, Error(`No entities were available at the URI "${entityURI}".`)];
    }
    const ret: Entity[] = [];
    for (const repr of catalogEntities) {
      const retEnt = EntityReprToEntity(repr);
      if (retEnt) {
        const [self, ok] = SelfLink(repr);
        if (ok && self) {
          retEnt.uri = self.uri;
          retEnt.selfURI = self.uri;
        }
        ret.push(retEnt);
      }
    }
    if (ret) {
      ret[0].eTag = etag;
    }
    return [ret, undefined];
  }

  logger.debug(`GetEntity(${entityURI}) error: ${err}`);
  return [undefined, err];
};

// 239
export const GetTableHistory = async (
  instanceID: string,
  dataProvider?: string
): Promise<[TableHistory | undefined, Error?]> => {
  let entityTypeID = CasTableEntityTypeID;
  if (dataProvider === DataSourcesProviderCompute) {
    entityTypeID = ComputeTableEntityTypeID;
  }
  const [entities, err] = await GetEntityHistory(entityTypeID, instanceID);
  if (err) {
    logger.debug(`There was an error getting the table history for instance ${instanceID}: ${err}`);
    return [undefined, err];
  }
  const analysisTimestamps: string[] = [];
  const rowCounts: (number | undefined)[] = [];
  const completenessPercents: (number | undefined)[] = [];
  if (entities) {
    for (const historyEntity of entities) {
      analysisTimestamps.push(historyEntity.modifiedTimeStamp!);
      rowCounts.push(historyEntity.attributes?.rowCount); // AttributeToDouble
      completenessPercents.push(historyEntity.attributes?.completenessPercent); // AttributeToDouble
    }
  }
  const ret: TableHistory = {
    analysisTimestamp: {
      label: TableHistoryAnalysisTimeStamp,
      numeric: false,
      values: analysisTimestamps,
    },
    completeness: {
      label: TableHistoryRowCount,
      numeric: true,
      values: completenessPercents,
    },
    rowCount: {
      label: TableHistoryCompletenessPercent,
      numeric: true,
      values: rowCounts,
    },
  };
  return [ret, undefined];
};

// 330
export const GetEntityHistory = async (
  definitionID: string,
  entityID: string,
  queryParams?: Record<string, string>
): Promise<[Entity[] | undefined, Error?]> => {
  const link: Link = {
    uri: `/catalog/definitions/${definitionID}/instances/${entityID}/history`,
    responseItemType: EntityMediaTypeJSON,
  };
  if (!queryParams) {
    queryParams = { limit: `${GO_MAX_INT32}` };
  }
  const [res, err] = await GetLinkCollectionWithQuery(link, queryParams, false);
  if (err) {
    logger.debug(
      `An error was returned when getting entity history for entity ${entityID}.  The error was ${err}`
    );
    return [undefined, err];
  }
  let ret: Entity[] | undefined;
  if (res && res.length > 0) {
    ret = [];
    // TODO: come back to once I figure out what this does...
    // let i = 0;
    for (const entity of res) {
      // var entityRep = &instance.EntityRepr{}
      // var decodeError = util.Decode(entity, entityRep)
      // if decodeError != nil {
      // 	logger.debug(`Skip returning history item ${i} due to a decode error: ${decodeError}`);
      // 	i++;
      // 	continue
      // }
      // i++;
      const entityRep = entity;
      ret.push(EntityReprToEntity(entityRep)!);
    }
  }
  return [ret, undefined];
};

// 358
const validateURI = (URI: string): Error | undefined => {
  if (!URI.startsWith(catalogURIPrefix)) {
    return Error(`The provided URI "${URI}" does not begin with the prefix "${catalogURIPrefix}"`);
  }
  return;
};

// 365
const isDirectInstanceURI = (URI: string): boolean => {
  return directEntityRegex.test(URI);
};
